import { EyeOutlined } from '@ant-design/icons';
import { App, Button, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { ProjectReportDetailDrawer } from '@/pages/admin/project-reports/ProjectReportDetailDrawer';
import {
  PROJECT_REPORT_STATUS_LABELS,
  projectReportStatusTagColor,
} from '@/constants/project-report';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import type { ProjectReportRecord } from '@/types/project-report';
import { formatDate } from '@/utils/format-date';

interface ProjectProgressTimelineSectionProps {
  projectId: string;
}

function excerpt(text: string | undefined, max = 56): string {
  const value = (text || '').trim();
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export function ProjectProgressTimelineSection({ projectId }: ProjectProgressTimelineSectionProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<ProjectReportRecord[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectReportsApi.fetchProjectReportsByProject(projectId, {
        page: 1,
        pageSize: 50,
        reportPeriod: 'DAILY',
      });
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载负责人进展失败';
      message.error(text);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [message, projectId]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const columns: ColumnsType<ProjectReportRecord> = [
    {
      title: '日期',
      dataIndex: 'reportDate',
      width: 120,
      render: (value: string) => formatDate(value),
    },
    {
      title: '汇报人',
      dataIndex: ['createdBy', 'name'],
      width: 100,
    },
    {
      title: '汇报进度',
      dataIndex: 'progressPercent',
      width: 90,
      render: (value: number) => `${value}%`,
    },
    {
      title: '进展说明',
      dataIndex: 'progressContent',
      render: (value: string) => excerpt(value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: ProjectReportRecord['status']) => (
        <Tag color={projectReportStatusTagColor(status)}>
          {PROJECT_REPORT_STATUS_LABELS[status]}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setDetailReportId(record.id);
            setDetailOpen(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        负责人写日报时填写的项目进展汇报（汇报进度 % 仅作展示，不覆盖任务进度）。
      </Typography.Paragraph>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={reports}
        pagination={false}
        locale={{ emptyText: '暂无负责人进展汇报' }}
      />
      <ProjectReportDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
        onChanged={() => void loadReports()}
      />
    </>
  );
}
